import { useState } from "react";
import toast from "react-hot-toast";
import { FolderIcon, UserIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { HOME } from "../../routes";
import { MdDownload } from "react-icons/md";
import { IoMdArrowRoundBack } from "react-icons/io";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  PROJECT_TYPES,
  REQUIRED_FIELDS,
  TOAST_MESSAGES,
  VALIDATION_MESSAGES,
} from "./constants";
import {
  createProject,
  previewExcel,
  uploadExcel,
} from "../../Services/projectServices";
import { FileUploader } from "../../Components/Common";
import { sample_excel } from "../../Assets/";
import { PreviewPopup } from "./Components";

const CreateProject = () => {
  const [previewData, setPreviewData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const navigate = useNavigate();

  const initialValues = {
    name: "",
    estimated_start_date: "",
    estimated_end_date: "",
    capacity: "",
    type: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    client_category: ""
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(REQUIRED_FIELDS.name),
    estimated_start_date: Yup.date().nullable(),
    estimated_end_date: Yup.date()
      .nullable()
      .min(
        Yup.ref("estimated_start_date"),
        "End date cannot be before start date"
      ),
    capacity: Yup.string().required(REQUIRED_FIELDS.CAPACITY),
    type: Yup.string().required(REQUIRED_FIELDS.TYPE),
    client_name: Yup.string().required(REQUIRED_FIELDS.CLIENT_NAME),
    client_email: Yup.string().email(REQUIRED_FIELDS.INVALID_EMAIL).nullable(),
    client_phone: Yup.string()
      .matches(/^[0-9]+$/, VALIDATION_MESSAGES.VALID_NUMBER)
      .min(10, "10-digit")
      .max(10, VALIDATION_MESSAGES.VALID_NUMBER)
      .required(REQUIRED_FIELDS.CLIENT_PHONE),
    client_address: Yup.string().required(REQUIRED_FIELDS.CLIENT_ADDRESS),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await createProject(values);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.PROJECT_CREATED);
        resetForm();
        navigate(HOME);
      } else {
        toast.error(TOAST_MESSAGES.PROJECT_CREATION_FAILED);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.PROJECT_CREATION_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnConfirm = async () => {
    if (!uploadedFile) {
      toast.error(TOAST_MESSAGES.NO_FILE_SELECTED);
      return;
    }
    try {
      const result = await uploadExcel(uploadedFile);
      if (result.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
        setShowPopup(false);
        navigate(HOME);
      } else {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
    }
  };

  const uploadFile = async (file) => {
      if (!file) return;
      try {
        const result = await previewExcel(file);
        if (result.status === 200) {
          setUploadedFile(file);
          setPreviewData(result.data?.preview);
          setShowPopup(true);
        } else {
          toast.error(result.error.error);
        }
      } catch (error) {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
    }

  return (
    <div className="mx-2 my-1 md:my-2">
      <div className="flex flex-row items-center justify-between gap-4 mb-2">
        <div className="flex justify-start gap-4 items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={20} className="text-primary" />
          </button>
          <h1 className="text-xl md:text-2xl text-center text-secondary font-semibold">
            Create New Project
          </h1>
        </div>

        {/* Right side: File Uploader */}
        {/* <div className="flex flex-row items-center gap-4 p-2">
          <div>
            <a
              href={sample_excel}
              download={"sample_project.xlsx"}
              target="download"
            >
              <MdDownload
                size={20}
                className="text-gray-500 hover:text-blue-600"
              />
            </a>
          </div>
          <div className="flex gap-4">
            <FileUploader
              label=""
              helperText=""
              accept=".xlsx,.xls"
              singleButton="true"
              btnText="Upload file"
              onUploadClick={(file) => uploadFile(file)}
            />
          </div>
        </div> */}
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={false}
      >
        <Form className="mx-2">
          {/* Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Project Details */}
            <div className="pr-0 md:pr-8 border-gray-200 lg:border-r">
              <div className="flex items-center justify-center gap-3 mb-4">
                <FolderIcon className="w-6 h-6 text-secondary" />
                <h2 className="text-xl font-semibold text-secondary">
                  Project Details
                </h2>
              </div>
              <div className="space-y-4">
                {/* Field Wrapper */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Project Name <sub className="text-red-500 top-0">*</sub>
                  </label>
                  <Field
                    name="name"
                    type="text"
                    placeholder="Enter project name"
                    className="border-b border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Capacity (KW) <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="capacity"
                      type="number"
                      placeholder="Enter project capacity"
                      className="border border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                    />
                    <ErrorMessage
                      name="capacity"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                  <div className="relative flex flex-col gap-1 z-10">
                    <label className="text-sm font-medium text-gray-700">
                      Type <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      as="select"
                      name="type"
                      className="focus:border-2 border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                    >
                      <option value="">Select type</option>
                      {PROJECT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="type"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Estimated Start Date{" "}
                      <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="estimated_start_date"
                      type="date"
                      className="border border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                    />
                    <ErrorMessage
                      name="estimated_start_date"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Estimated End Date{" "}
                      <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="estimated_end_date"
                      type="date"
                      className="border border-gray-300  px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                    />
                    <ErrorMessage
                      name="estimated_end_date"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Client Details */}
            <div className="pl-0 lg:pl-8 border-gray-200 lg:border-l mt-8 md:mt-0">
              <div className="flex items-center justify-center gap-3 mb-4">
                <UserIcon className="w-6 h-6 text-secondary" />
                <h2 className="text-xl font-medium text-secondary">
                  Client Details
                </h2>
              </div>
              <div className="space-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Client Name <sub className="text-red-500 top-0">*</sub>
                  </label>
                  <Field
                    name="client_name"
                    type="text"
                    placeholder="Enter client name"
                    className="border border-gray-300  px-3 py-2 text-sm focus:ring-1 focus:border-secondary focus:ring-secondary focus:outline-none"
                  />
                  <ErrorMessage
                    name="client_name"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <Field
                      name="client_email"
                      type="email"
                      placeholder="Enter client email"
                      className="border border-gray-300  px-3 py-2 text-sm focus:ring-1 focus:border-secondary focus:ring-secondary focus:outline-none"
                    />
                    <ErrorMessage
                      name="client_email"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Mobile <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="client_phone"
                      type="number"
                      placeholder="Enter client mobile"
                      className="border border-gray-300  px-3 py-2 text-sm focus:ring-1 focus:border-secondary focus:ring-secondary focus:outline-none"
                    />
                    <ErrorMessage
                      name="client_phone"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Address <sub className="text-red-500 top-0">*</sub>
                  </label>
                  <Field
                    as="textarea"
                    rows="3"
                    name="client_address"
                    placeholder="Enter client address"
                    className="border border-gray-300  px-3 py-2 text-sm focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary"
                  />
                  <ErrorMessage
                    name="client_address"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 mt-4 border-gray-200">
            <button
              type="submit"
              className="px-5 py-2  bg-primary text-white shadow hover:shadow-lg hover:bg-secondary active:scale-95 transition-all text-sm font-medium"
            >
              Save Project
            </button>
          </div>
        </Form>
      </Formik>

      {showPopup && (
        <PreviewPopup
          popUpData={previewData}
          onClose={() => {
            setShowPopup(false);
            setPreviewData(null);
            setUploadedFile(null);
          }}
          onConfirm={handleOnConfirm}
        />
      )}
    </div>
  );
};

export default CreateProject;
